import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Modal,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  addGroupBooking,
  authorizedPost,
  getSubAgentsForDropdown,
  getValuesForOptions,
} from "../../store/actions";
import { LIST_ALL_ROOMTYPE_BOOKING_ENGINE_OFFERS, LIST_MOKSHA_ROOMTYPE_RATES } from "../../graphql";
import { CssTextField } from "../../components/TextField";
import { Add, ArrowBack, Done } from "@mui/icons-material";
import { tokens } from "../../theme";
import { toast } from "react-toastify";
import AddNewSubAgentModalContent from "../agents/addNewSubAgentModalContent";
import AddNewAgentModalContent from "../agents/addNewAgentModalContent";
import titles from "../../data/nameTitles";
import SearchAndSelectInput from "../../components/SearchAndSelect";
import PlusOrMinusButton from "../../components/PlusOrMinusButton";
import moment from "moment";
import AsyncSelectSearch from "../../components/AsyncSelectSearch";
import ConfirmDialog from "../../components/ConfirmDialog";
import AddNewCompanyModalContent from "../companies/AddNewCompanyModalContent";
import MokshaDayWiseRates from "./mokshaDayWiseRates";
import {
  belowRackCheck,
  dayWiseMismatch,
  dayWisePayload,
  dayWiseProblem,
  nightsOf,
  numberOf,
  rackRateOf,
  rackRateTotal,
} from "./mokshaDayWise";
import { BASES } from "../moksha-reservation/reservationDraft";

const initialValues = {
  name: "",
  email: "",
  title: "Mr",
  // cutOffDays: 7,
  cutOffDate: moment().format("YYYY-MM-DD"),
};

function GroupBookingPage2({
  roomsData,
  totalAmount,
  agentsList,
  subAgentsList,
  isLoading,
  setPage,
  navigate,
  companiesList,
}) {
  const theme = useTheme();
  const cutOffRef = useRef();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const [status, setStatus] = useState("confirmed");
  const [values, setValues] = useState(initialValues);
  const [agent, setAgent] = useState("");
  const [subAgent, setSubAgent] = useState("");
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [addAgentModal, setAddAgentModal] = useState(false);
  const [addSubAgentModal, setAddSubAgentModal] = useState(false);
  const [extraBedsEnabled, setExtraBedsEnabled] = useState(false);
  const [extraBeds, setExtraBeds] = useState(1);
  const [cutOffEnabled, setCutOffEnabled] = useState(false);
  const [company, setCompany] = useState("");
  const [companyEnabled, setCompanyEnabled] = useState(false);
  const [addCompanyModal, setAddCompanyModal] = useState(false);
  const [isComplimentary, setIsComplimentary] = useState(false);
  const [bookingFrom, setBookingFrom] = useState("direct_booking");
  const [mokshaOffers, setMokshaOffers] = useState([]);
  // { [roomtypeId]: Moksha rate rows }; a roomtype without one falls back to its booking engine rate.
  const [mokshaRates, setMokshaRates] = useState({});
  // { [roomIndex]: { [nightUnix]: { amount, extra_bed_price, meal_plan_price } } } — Moksha only, inclusive of tax.
  const [dayWise, setDayWise] = useState({});
  const [mokshaLoading, setMokshaLoading] = useState(false);
  // { rack, roomExGst, percentBelow, data } while a below-rack sale awaits confirmation.
  const [belowRack, setBelowRack] = useState(null);
  const [mokshaBasis, setMokshaBasis] = useState("rack");

  const hotelProfile = useSelector((state) => state.registration?.hotelProfile);
  const isMokshaEnabled = hotelProfile?.whatsapp_booking_enabled === true;
  // Unset counts as enabled, as the payout's gstEnabled default does.
  const gstEnabled = hotelProfile?.gst_enabled !== false;
  const loggedInUser = useSelector((state) => state.auth.loggedInUser);
  const isMokshaStaff = loggedInUser?.is_moksha_staff === true;

  // Agent and company are derived from their switches, so they replace the manual
  // sources rather than sitting alongside them. Moksha is a real source and can be
  // chosen either way - a moksha booking may also carry an agent or a company.
  const bookingFromOptions = [
    ...(agentEnabled || companyEnabled
      ? []
      : [
          { value: "direct_booking", label: "Direct Booking" },
          { value: "walk_in", label: "Walk In" },
          { value: "friends_family", label: "Friends/Family" },
        ]),
    ...(agentEnabled ? [{ value: "agent", label: "Agent" }] : []),
    ...(companyEnabled ? [{ value: "company", label: "Company" }] : []),
    ...(isMokshaEnabled && isMokshaStaff ? [{ value: "moksha", label: "Moksha" }] : []),
  ];

  // const [formValues, setFormValues] = useState({});
  // eslint-disable-next-line
  const [temp, setTemp] = useState("");

  useEffect(() => {
    if (bookingFrom === "moksha") {
      fetchMokshaOffers();
    } else {
      setMokshaOffers([]);
      setMokshaRates({});
    }
    // eslint-disable-next-line
  }, [bookingFrom]);

  const fetchMokshaOffers = async () => {
    setMokshaLoading(true);
    try {
      const roomtypeIds = [...new Set(roomsData.arr.map((r) => r.roomtype_id))];
      const ratesByRoomtype = {};
      // Roomtypes are read in parallel; offers are flattened in roomtype order, as before.
      const offersByRoomtype = await Promise.all(
        roomtypeIds.map(async (roomtypeId) => {
          // A failed read leaves this roomtype on its booking engine rate.
          try {
            const ratesResponse = await authorizedPost(LIST_MOKSHA_ROOMTYPE_RATES, {
              roomtype_id: roomtypeId,
            });
            ratesByRoomtype[roomtypeId] =
              ratesResponse?.data?.list_moksha_roomtype_rates?.data?.rates || [];
          } catch (err) {
            ratesByRoomtype[roomtypeId] = [];
          }
          const response = await authorizedPost(LIST_ALL_ROOMTYPE_BOOKING_ENGINE_OFFERS, {
            roomtypeId,
            pageNumber: 1,
            pageSize: 100,
          });
          const offers = response?.data?.list_all_roomtype_booking_engine_offers?.data || [];
          const activeOffers = offers.filter((o) => o.is_active);
          const checkinDate = roomsData.arr.find((r) => r.roomtype_id === roomtypeId)?.checkin_date;
          const matchingOffers = activeOffers.filter((o) => {
            if (o.type === "booking_engine_seasonal" && checkinDate) {
              return checkinDate >= o.start_date && checkinDate <= o.end_date;
            }
            return o.type === "booking_engine_weekdays";
          });
          return matchingOffers.map((o) => ({ ...o, roomtype_id: roomtypeId }));
        }),
      );
      const allOffers = offersByRoomtype.flat();
      setMokshaOffers(allOffers);
      setMokshaRates(ratesByRoomtype);
      // Rack is a stay total, and it is only a default - a rate already typed stands.
      const rackTotal = rackRateTotal(roomsData.arr, allOffers, ratesByRoomtype);
      if (rackTotal > 0) {
        setValues((prev) =>
          prev.moksha_rack_rate ? prev : { ...prev, moksha_rack_rate: rackTotal },
        );
      }
    } catch (err) {
      console.error("Error fetching moksha offers:", err);
    }
    setMokshaLoading(false);
  };

  const handleChange = (event) => {
    let fieldname = event?.target?.name;
    setValues((prev) => ({ ...prev, [fieldname]: event?.target?.value }));
    setTemp(event.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (agentEnabled && agent === "") {
      return toast.error("Please select an agent");
    }
    if (companyEnabled && company === "") {
      return toast.error("Please select a company");
    }

    if (
      cutOffEnabled &&
      moment().format("YYYY-MM-DD") !==
        moment.unix(roomsData.arr[0].checkin_date).format("YYYY-MM-DD")
    ) {
      if (
        moment().format("YYYY-MM-DD") ===
        moment(values.cutOffDate).format("YYYY-MM-DD")
      ) {
        return toast.error(
          "Cut off date and checkin date must not be the same"
        );
      }
    }

    // Moksha stays the source even when an agent or company is attached - the ids are
    // still sent and persisted. Everything else derives from the switches.
    let finalBookingFrom = bookingFrom;
    if (bookingFrom !== "moksha") {
      if (agentEnabled) {
        finalBookingFrom = "agent";
      } else if (companyEnabled) {
        finalBookingFrom = "company";
      }
    }

    let data = {
      ...values,
      status,
      agent_id: agentEnabled && agent !== "no_preference" ? agent : "",
      sub_agent_id:
        agentEnabled && subAgent !== "no_preference" ? subAgent : "",
      company_id: companyEnabled && company !== "no_preference" ? company : "",
      is_complimentary: isComplimentary,
      booking_from: finalBookingFrom,
      rooms: roomsData.arr.map((e) => ({
        checkin_date: e.checkin_date,
        checkout_date: e.checkout_date,
        room_id: e.room_id,
        room_number: e.room_number,
        no_of_adults: e.no_of_adults,
        no_of_childs: e.no_of_childs,
        meal_plan_id: e.meal_plan_id || "",
      })),
      cutOffDate:
        cutOffEnabled && values.cutOffDate
          ? moment(values.cutOffDate, "YYYY-MM-DD").startOf("day").unix()
          : 0,
    };
    if (data.paid_amount === "") {
      delete data.paid_amount;
    }
    // Only the chosen basis's rate is sent; the rack prefill sits in values whatever the basis.
    const isContract = finalBookingFrom === "moksha" && mokshaBasis === "contract";
    if (isContract) {
      const contractRate = numberOf(data.moksha_contract_rate);
      // Complimentary is priced at 0, so it needs no rate.
      if (!isComplimentary && !(contractRate > 0)) {
        return toast.error(BASES.contract.missing);
      }
      data.moksha_commission_basis = "contract";
      data.moksha_contract_rate = contractRate;
      delete data.moksha_rack_rate;
    } else if (finalBookingFrom === "moksha") {
      data.moksha_commission_basis = "rack";
      data.moksha_rack_rate = numberOf(data.moksha_rack_rate);
      delete data.moksha_contract_rate;
    } else {
      delete data.moksha_rack_rate;
      delete data.moksha_contract_rate;
    }
    // The special price is the source, so the nights must still add up to it.
    if (finalBookingFrom === "moksha" && !isComplimentary) {
      const rooms = roomsData?.arr ?? [];
      const { price, total, mismatch } = dayWiseMismatch(rooms, dayWise, data.custom_price);
      // A split that cannot be built is refused here rather than dropped from the payload. Checked
      // first so a negative extra is named instead of surfacing as a total mismatch.
      const problem = price > 0 ? dayWiseProblem(rooms, dayWise) : null;
      if (problem) {
        return toast.error(problem);
      }
      if (mismatch) {
        return toast.error(
          `Day-wise total is ₹${total.toFixed(2)} but the special price is ₹${price.toFixed(2)}`,
        );
      }
      if (price > 0) {
        const splitRooms = dayWisePayload(rooms, dayWise);
        // The room each split was entered for travels with it; the API refuses it on any other room.
        data.rooms = data.rooms.map((room, i) =>
          splitRooms[i]
            ? {
                ...room,
                ...(rooms[i]?.room_no != null ? { room_no: String(rooms[i].room_no) } : {}),
                ...(rooms[i]?.roomtype_id ? { roomtype_id: rooms[i].roomtype_id } : {}),
                day_wise_details: splitRooms[i],
              }
            : room,
        );
      }
    }
    if (data.custom_price === "") {
      delete data.custom_price;
    }
    // if (!cutOffEnabled || data.cutOffDays === ""){
    //     delete data.cutOffDays;
    // }
    if (extraBedsEnabled) {
      data.extra_beds = extraBeds;
    }
    console.log(data);

    // Checked on the nights actually sent; without a special price there are none, so no check.
    if (finalBookingFrom === "moksha" && !isContract && !isComplimentary) {
      const below = belowRackCheck(
        data.rooms.flatMap((room) => (room.day_wise_details || []).map((night) => night.amount)),
        data.moksha_rack_rate,
        gstEnabled,
      );
      if (below) {
        setBelowRack({ ...below, data });
        return;
      }
    }
    await submitBooking(data);
  };

  const submitBooking = async (data) => {
    let res = await dispatch(addGroupBooking(data));
    if (res) {
      setValues(initialValues);
      setMokshaBasis("rack");
      localStorage.removeItem("grpBooking");
      navigate("/calendar");
      window.location.reload();
    }
  };

  const handleAgentChange = (agent_id) => {
    const item = agentsList?.find((a) => a?._id === agent_id);


    setAgent(agent_id);
    setSubAgent("");

    if (!item) return;

    // Auto-populate booking details
    setValues((prev) => ({
      ...prev,
      // name: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
      email: item.email || "",
      guest_mobile: item.mobile || "",
      guest_address: item.address || "",
      gst_number: item.gst_number || "",
      title: "Mr", // default
    }));
  };

  const handleCompanyChange = (company_id) => {
    const item = companiesList.list?.find((c) => c?._id === company_id);

    console.log("Selected Company:", item);

    setCompany(company_id);

    if (!item) return;

    // Auto-populate booking details from company
    setValues((prev) => ({
      ...prev,
      name: item.name || "",
      email: item.email || "",
      guest_mobile: item.mobile || "",
      guest_address: item.address || "",
      gst_number: item.gst_number || "",
      title: "Mr",
    }));
  };

  const fetchSubAgents = async () => {
    if (agent !== "") {
      dispatch(getSubAgentsForDropdown({ id: agent }));
    }
  };
  useEffect(() => {
    fetchSubAgents();
    // eslint-disable-next-line
  }, [agent]);

  const renderOption = (item) => (
    <Box display="flex" flexDirection="column" p={1}>
      <Typography variant="body1" style={{ color: "#fff" }}>
        {item.name}
      </Typography>
      <Typography variant="body2" style={{ color: "#fff" }}>
        {item.email}
      </Typography>
      <Typography variant="body2" style={{ color: "#cccccc" }}>
        {item.mobile}
      </Typography>
    </Box>
  );

  const handleSelect = (item) => {
    if (values?.email) {
      if (values.email) {
        setValues((prev) => ({
          ...prev,
          name: item?.name || "",
        }));
      }
    } else {
      setValues((prev) => ({
        ...prev,
        name: item?.name || "",
        email: item?.email || "",
        guest_mobile: item?.mobile || "",
        guest_address: item?.address || "",
        preference: item?.preference || "",
        title: item?.title || "Mr",
      }));
    }
  };

  const onSearch = async (query) => {
    try {
      let result = await dispatch(getValuesForOptions(query));
      return result || [];
    } catch (e) {
      console.error("Search error:", e);
      return [];
    }
  };

  // Names the rate behind the prefill, for the first room that has one.
  const rackHint = (roomsData?.arr ?? [])
    .filter((room) => nightsOf(room).length > 0)
    .map((room) => rackRateOf(room, mokshaOffers, mokshaRates))
    .find(({ rate }) => rate > 0);
  const typedRackRate = numberOf(values?.moksha_rack_rate);
  const typedContractRate = numberOf(values?.moksha_contract_rate);
  const mokshaPriceHint =
    mokshaBasis === "contract"
      ? typedContractRate > 0
        ? `Contract rate: ₹${typedContractRate} (incl. GST, whole stay)`
        : "Contract rate not set"
      : typedRackRate > 0
        ? `Rack rate: ₹${typedRackRate} (ex-GST, whole stay)`
        : "Rack rate not set";

  const preventSubmitOnEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} onKeyDown={preventSubmitOnEnter}>
        <Grid container columnSpacing={{ md: 4, xs: 2 }} rowSpacing={2}>
          <Grid item md={6} xs={12}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>Title:</Typography>
                <CssTextField
                  select
                  fullWidth
                  required
                  size="small"
                  name="title"
                  onChange={handleChange}
                  value={values.title}
                >
                  {titles?.map((title, index) => (
                    <MenuItem value={title} key={index}>
                      {title}
                    </MenuItem>
                  ))}
                </CssTextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>Name: *</Typography>
                <AsyncSelectSearch
                  onSearch={onSearch}
                  renderOption={renderOption}
                  onSelectResult={handleSelect}
                  placeholder="Search for names..."
                  selectStyles={{
                    control: (provided) => ({
                      ...provided,
                      borderRadius: 4,
                      minWidth: 340,

                      //   maxWidth: 500,
                      marginLeft: 1,
                    }),
                    menu: (provided) => ({
                      ...provided,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }),
                  }}
                  required
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>{`Email:`}</Typography>
                <CssTextField
                  fullWidth
                  // required={!agentEnabled}
                  size="small"
                  type="email"
                  placeholder={`Email`}
                  name="email"
                  value={values?.email || ""}
                  onChange={handleChange}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>{`Mobile:`}</Typography>
                <CssTextField
                  fullWidth
                  // required={!agentEnabled}
                  size="small"
                  type="number"
                  placeholder={`Mobile`}
                  name="guest_mobile"
                  value={values?.guest_mobile || ""}
                  onChange={handleChange}
                />
              </Stack>
              <Stack direction="row" alignItems="center">
                <Typography noWrap>Complimentary booking</Typography>
                <Switch
                  checked={isComplimentary}
                  onChange={(e) => {
                    setIsComplimentary(e.target.checked);

                    // Reset prices immediately
                    if (e.target.checked) {
                      setValues((prev) => ({
                        ...prev,
                        paid_amount: 0,
                        custom_price: "",
                      }));
                    }
                  }}
                />
              </Stack>
              {bookingFromOptions.length > 0 && (
                <>
                  <Stack direction="row" spacing={2}>
                    <Typography width={"70%"}>Booking From:</Typography>
                    <CssTextField
                      fullWidth
                      select
                      size="small"
                      value={bookingFrom}
                      onChange={(e) => setBookingFrom(e.target.value)}
                    >
                      {bookingFromOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </CssTextField>
                  </Stack>
                  {bookingFrom === "moksha" && (
                    <Stack direction="row" spacing={2}>
                      <Typography width={"70%"}>Commission basis:</Typography>
                      <CssTextField
                        fullWidth
                        select
                        size="small"
                        value={mokshaBasis}
                        onChange={(e) => setMokshaBasis(e.target.value)}
                      >
                        {Object.entries(BASES).map(([value, { label }]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </CssTextField>
                    </Stack>
                  )}
                  {bookingFrom === "moksha" && mokshaBasis === "contract" && (
                    <Stack direction="row" spacing={2}>
                      <Typography width={"70%"}>Contract Rate:</Typography>
                      <CssTextField
                        fullWidth
                        required={!isComplimentary}
                        size="small"
                        type="number"
                        placeholder="Contract Rate"
                        name="moksha_contract_rate"
                        value={values?.moksha_contract_rate ?? ""}
                        onChange={handleChange}
                        InputProps={{ inputProps: { min: 0, step: "any" } }}
                        helperText={BASES.contract.rateLabel}
                        disabled={isComplimentary}
                      />
                    </Stack>
                  )}
                  {bookingFrom === "moksha" && mokshaBasis === "rack" && (
                    <Stack direction="row" spacing={2}>
                      <Typography width={"70%"}>Rack Rate:</Typography>
                      <CssTextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Rack Rate"
                        name="moksha_rack_rate"
                        value={values?.moksha_rack_rate ?? ""}
                        onChange={handleChange}
                        InputProps={{
                          inputProps: { min: 0 },
                          endAdornment: mokshaLoading ? <CircularProgress size={16} /> : null,
                        }}
                        helperText={
                          rackHint
                            ? `${rackHint.source === "moksha" ? "Moksha" : "BKE"} Rate: ₹${rackHint.rate} / night. Rack rate is the whole stay.`
                            : "No Moksha or booking engine rate found"
                        }
                        disabled={isComplimentary}
                      />
                    </Stack>
                  )}
                </>
              )}
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>Special price:</Typography>
                <CssTextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="Special price"
                  name="custom_price"
                  value={values?.custom_price ?? ""}
                  onChange={handleChange}
                  InputProps={{ inputProps: { min: 0, step: "any" } }}
                  helperText={
                    bookingFrom === "moksha" ? mokshaPriceHint : `Rack rate: ${totalAmount}`
                  }
                  disabled={isComplimentary}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>Status:</Typography>
                <CssTextField
                  fullWidth
                  select
                  size="small"
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value={"confirmed"}>{"Confirmed"}</MenuItem>
                  <MenuItem value={"hold"}>{"Block"}</MenuItem>
                </CssTextField>
              </Stack>
              {bookingFrom === "moksha" && (
                <MokshaDayWiseRates
                  rooms={roomsData?.arr ?? []}
                  entered={dayWise}
                  setEntered={setDayWise}
                  specialPrice={values?.custom_price}
                  disabled={isComplimentary}
                  colors={colors}
                />
              )}
            </Stack>
          </Grid>
          <Grid item md={6} xs={12}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>Preference:</Typography>
                <CssTextField
                  fullWidth
                  size="small"
                  type="text"
                  placeholder="Preference"
                  name="preference"
                  value={values.preference || ""}
                  onChange={handleChange}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>Billing Instruction:</Typography>
                <CssTextField
                  fullWidth
                  size="small"
                  type="text"
                  placeholder="Billing Instruction"
                  name="billingInstruction"
                  value={values.billingInstruction || ""}
                  onChange={handleChange}
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>Address:</Typography>
                <CssTextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  type="text"
                  placeholder="Address"
                  name="guest_address"
                  value={values?.guest_address || ""}
                  onChange={handleChange}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography width={"70%"}>Remarks:</Typography>
                <CssTextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  type="text"
                  placeholder="Remarks"
                  name="remarks"
                  value={values?.remarks || ""}
                  onChange={handleChange}
                />
              </Stack>
              <Stack direction="row" alignItems="center">
                <Typography noWrap>Agent booking</Typography>
                <Switch
                  checked={agentEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setAgentEnabled(enabled);

                    if (enabled) {
                      setCompanyEnabled(false);
                      setCompany(""); // clear company selection
                      if (bookingFrom !== "moksha") setBookingFrom("agent");
                    } else {
                      setAgent("");
                      setSubAgent("");
                      if (bookingFrom === "agent")
                        setBookingFrom("direct_booking");
                    }
                  }}
                />
              </Stack>
              {agentEnabled && (
                <>
                  <Stack direction="row" spacing={2}>
                    <Typography width={"70%"}>Agent / Agency:</Typography>
                    <SearchAndSelectInput
                      keyname="first_name"
                      value={agent}
                      required={agentEnabled}
                      list={agentsList}
                      onChange={(id) => handleAgentChange(id)}
                    />
                    <Button
                      variant="outlined"
                      color="primary"
                      title="Add new agent"
                      onClick={() => setAddAgentModal(true)}
                    >
                      <Add />
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <Typography width={"70%"}>Sub Agent:</Typography>
                    <SearchAndSelectInput
                      keyname="name"
                      value={subAgent}
                      // required={agentEnabled}
                      list={subAgentsList}
                      disabled={!agent || agent === ""}
                      onChange={(id) => setSubAgent(id)}
                    />
                    <Button
                      variant="outlined"
                      color="primary"
                      title="Add new sub agent"
                      onClick={() => setAddSubAgentModal(true)}
                      disabled={!agent || agent === ""}
                    >
                      <Add />
                    </Button>
                  </Stack>
                </>
              )}

              {/* START COMPANY SECTION */}
              <Stack direction="row" alignItems="center">
                <Typography noWrap>Company booking</Typography>
                <Switch
                  checked={companyEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setCompanyEnabled(enabled);

                    if (enabled) {
                      setAgentEnabled(false);
                      setAgent(""); // clear agent
                      setSubAgent(""); // clear sub-agent
                      if (bookingFrom !== "moksha") setBookingFrom("company");
                    } else {
                      setCompany("");
                      if (bookingFrom === "company")
                        setBookingFrom("direct_booking");
                    }
                  }}
                />
              </Stack>
              {companyEnabled && (
                <Stack direction="row" spacing={2}>
                  <Typography width={"70%"}>Company:</Typography>
                  <SearchAndSelectInput
                    keyname="name" // Assuming your company object has a 'name' field
                    value={company}
                    required={companyEnabled}
                    list={companiesList.list} // Ensure this list is passed via props
                    // onChange={(id) => setCompany(id)}
                    onChange={(id) => handleCompanyChange(id)}
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    title="Add new company"
                    onClick={() => setAddCompanyModal(true)}
                  >
                    <Add />
                  </Button>
                </Stack>
              )}

              <Stack direction="row" alignItems="center">
                <Typography noWrap>Extra beds</Typography>
                <Switch
                  checked={extraBedsEnabled}
                  onChange={(e) => setExtraBedsEnabled(e.target.checked)}
                />
              </Stack>
              {extraBedsEnabled && (
                <Stack direction="row" spacing={2}>
                  <Typography width={"70%"}>Number of extra beds:</Typography>
                  <PlusOrMinusButton
                    min={1}
                    max={10}
                    size="small"
                    name="extraBeds"
                    value={extraBeds}
                    handleChange={(value) => setExtraBeds(value)}
                  />
                </Stack>
              )}
              <Stack direction="row" alignItems="center">
                <Typography noWrap>Cut off</Typography>
                <Switch
                  checked={cutOffEnabled}
                  onChange={(e) => setCutOffEnabled(e.target.checked)}
                />
              </Stack>
              {cutOffEnabled && (
                <Stack direction="row" spacing={2}>
                  <Typography width={"70%"}>Cut off date:</Typography>
                  <CssTextField
                    fullWidth
                    required
                    size="small"
                    type="date"
                    // placeholder="Cut off date"
                    // label="Cut off date"
                    onChange={handleChange}
                    name="cutOffDate"
                    value={values.cutOffDate}
                    inputProps={{
                      min: moment().format("YYYY-MM-DD"),
                      max: moment
                        .unix(roomsData.arr[0].checkin_date)
                        .format("YYYY-MM-DD"),
                    }}
                    // error={!!touched.cutOffDate && !!errors.cutOffDate}
                    // helperText={touched.cutOffDate && errors.cutOffDate}
                    inputRef={cutOffRef}
                    onClick={() => cutOffRef.current.showPicker()}
                  />
                  {/* <CssTextField
                                        fullWidth
                                        required
                                        size="small"
                                        type="number"
                                        placeholder="Cut off period (in days)"
                                        name="cutOffDays"
                                        value={values.cutOffDays || ""}
                                        onChange={handleChange}
                                        InputProps={{ inputProps: { min: 0 } }}
                                    /> */}
                </Stack>
              )}
            </Stack>
          </Grid>
          <Grid item md={12} xs={12}>
            <Stack direction="row" justifyContent="space-between">
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => setPage(1)}
              >
                <b>Go back</b>
              </Button>
              {/* Held until the rack prefill lands, or the below-rack check would see no rack. */}
              <Button type="submit" variant="contained" endIcon={<Done />} disabled={mokshaLoading}>
                {isLoading ? (
                  <CircularProgress color="inherit" size={24} />
                ) : (
                  <b>Book Now</b>
                )}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>

      <Modal open={addAgentModal} onClose={() => setAddAgentModal(false)}>
        <>
          <AddNewAgentModalContent
            dispatch={dispatch}
            isLoading={isLoading}
            setModal={setAddAgentModal}
            setAgent={setAgent}
            colors={colors}
          />
        </>
      </Modal>

      <Modal open={addSubAgentModal} onClose={() => setAddSubAgentModal(false)}>
        <>
          <AddNewSubAgentModalContent
            id={agent}
            dispatch={dispatch}
            isLoading={isLoading}
            setModal={setAddSubAgentModal}
            setSubAgent={setSubAgent}
            colors={colors}
          />
        </>
      </Modal>
      <Modal open={addCompanyModal} onClose={() => setAddCompanyModal(false)}>
        <>
          <AddNewCompanyModalContent
            dispatch={dispatch}
            isLoading={isLoading}
            setModal={setAddCompanyModal}
            setCompany={setCompany}
            colors={colors}
          />
        </>
      </Modal>
      <ConfirmDialog
        open={Boolean(belowRack)}
        title="Booking below rack rate"
        message={`The total amount is ${belowRack?.percentBelow?.toFixed(2)}% less than the rack rate. Are you sure you want to continue?`}
        onCancel={() => setBelowRack(null)}
        onConfirm={() => {
          const { data } = belowRack;
          setBelowRack(null);
          submitBooking(data);
        }}
      />
    </>
  );
}

export default GroupBookingPage2;
